import {
  TaskDTO,
  TaskStatusInput,
  TaskType,
} from "../../../../vl-api/vlSchemas";
import {
  StatusCellTooltip,
  StatusColor,
} from "views/uikit/VLTable/TableCells/StatusCell/StatusCell";

export const getTaskNameByTaskType: Record<TaskType, string> = {
  DATASET_CREATION: "Dataset Creation",
  MEDIA_ADDITION: "Media Addition",
  ENRICHMENT: "Enriching",
  LABEL_PROPAGATION: "Label Propagation",
  TRAINING: "Training",
  CUSTOM_METADATA: "Custom metadata",
  REINDEX: "Reindex",
  SNAPSHOT_CLONE: "Snapshot Clone",
  SNAPSHOT_RESTORE: "Snapshot Restore",
  MODEL_VALIDATION: "Model Validation",
};

export const getStatusColor = (status: TaskStatusInput): StatusColor => {
  const statusColors = {
    PENDING: "processing",
    INITIALIZING: "processing",
    RUNNING: "processing",
    COMPLETED: "success",
    FAILED: "error",
    ABORTED: "error",
    WAITING_FOR_REVIEW: "warning",
  };
  return statusColors[status] as StatusColor;
};

export const statusPriorityByTaskStatus: Record<TaskStatusInput, number> = {
  PENDING: 0,
  INITIALIZING: 1,
  RUNNING: 2,
  FAILED: 3,
  ABORTED: 4,
  WAITING_FOR_REVIEW: 5,
  COMPLETED: 6,
};

export const canAbortTask = (
  status?: TaskStatusInput | null,
  taskType?: TaskType | null,
  taskCreatedBy?: string | null,
  currentUserEmail?: string | null,
): boolean => {
  const abortableStatuses = ["PENDING", "RUNNING", "INITIALIZING"];
  const abortableTaskTypes = ["DATASET_CREATION"];
  if (!status || !taskType) {
    return false;
  }

  if (taskCreatedBy && currentUserEmail && taskCreatedBy !== currentUserEmail) {
    return false;
  }

  return (
    abortableStatuses.includes(status) && abortableTaskTypes.includes(taskType)
  );
};

export const getTextByStatus: Record<
  TaskStatusInput,
  (percent?: number) => string
> = {
  PENDING: () => "Pending",
  INITIALIZING: (percent?: number) =>
    percent ? `${percent}% Initializing` : "Initializing",
  RUNNING: (percent?: number) => (percent ? `${percent}% Running` : "Running"),
  COMPLETED: () => "Completed",
  FAILED: () => "Failed",
  ABORTED: () => "Aborted",
  WAITING_FOR_REVIEW: () => "Waiting for review",
};

export const getTooltipByStatus: Record<
  TaskStatusInput,
  (msg?: string) => StatusCellTooltip | undefined
> = {
  PENDING: () => ({
    title: "Pending",
    text: "Waiting for resources to become available",
  }),
  INITIALIZING: () => undefined,
  RUNNING: () => undefined,
  ABORTED: (msg?: string) => {
    return { title: "Aborted", text: msg || "Something went wrong" };
  },
  FAILED: (msg?: string) => {
    return { title: "Failed", text: msg || "Something went wrong" };
  },
  COMPLETED: () => undefined,
  WAITING_FOR_REVIEW: () => undefined,
};

export const getLoadingByStatus: Record<TaskStatusInput, boolean> = {
  PENDING: true,
  INITIALIZING: false,
  RUNNING: true,
  COMPLETED: false,
  FAILED: false,
  ABORTED: false,
  WAITING_FOR_REVIEW: false,
};

const getDatasetUrl = (
  datasetId: string,
  queryParams: Record<string, string> = {},
) => {
  const params = new URLSearchParams({ page: "1", ...queryParams });
  return `/dataset/${datasetId}/data?${params.toString()}`;
};

const getDatasetCreationUrl = (data: TaskDTO): string => {
  if (data.status === "FAILED") return "datasets";
  if (data.status !== "COMPLETED") return `/dataset/${data.dataset_id}/create`;
  return getDatasetUrl(data.dataset_id);
};

const getLabelPropagationUrl = (data: TaskDTO): string => {
  const queryParams: Record<string, string> = {};
  if (data.reference_id) queryParams.seed = data.reference_id;
  if (data.flow_id) queryParams.flow = data.flow_id;
  return getDatasetUrl(data.dataset_id, queryParams);
};

const getLabelEnrichmentUrl = (data: TaskDTO): string | undefined => {
  if (data.status === "COMPLETED") return getDatasetUrl(data.dataset_id);
  if (["ABORTED", "FAILED"].includes(data.status)) return;
  return `/dataset/${data.dataset_id}/enrich`;
};

export const getHrefByTaskType: Record<
  TaskType,
  (data: TaskDTO) => string | undefined
> = {
  DATASET_CREATION: getDatasetCreationUrl,
  CUSTOM_METADATA: (data) => getDatasetUrl(data.dataset_id),
  ENRICHMENT: (data) => getLabelEnrichmentUrl(data),
  LABEL_PROPAGATION: getLabelPropagationUrl,
  MEDIA_ADDITION: (data) => getDatasetUrl(data.dataset_id),
  TRAINING: (data) => getDatasetUrl(data.dataset_id),
  REINDEX: (data) => getDatasetUrl(data.dataset_id),
  SNAPSHOT_CLONE: getDatasetCreationUrl,
  SNAPSHOT_RESTORE: (data) => getDatasetUrl(data.dataset_id),
  MODEL_VALIDATION: (data) => getDatasetUrl(data.dataset_id),
};
